import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import { useToast } from '../contexts/ToastContext'
import { Badge, Button, Modal } from '../components'
import {
  fetchArtworkTemplates,
  createArtworkTemplate,
  type ArtworkTemplateSummary,
  type ArtworkTemplateType,
} from '../services/api'
import { CANVAS_PRESETS } from '../utils/artworkTemplate'
import pageStyles from '../styles/page.module.scss'
import styles from './ArtworkTemplatesList.module.scss'

const TYPE_LABELS: Record<ArtworkTemplateType, string> = {
  matchcard: 'Match cards',
  vod_overlay: 'VOD overlays',
  hero: 'Hero cards',
  plate: 'Plates',
  draw: 'Draws',
}
// 'plate' and 'draw' are reserved — no renderer consumes them yet, so they sort
// last and aren't offered when creating.
const TYPE_ORDER: ArtworkTemplateType[] = ['matchcard', 'vod_overlay', 'hero', 'plate', 'draw']
const CREATABLE_TYPES: ArtworkTemplateType[] = ['matchcard', 'vod_overlay', 'hero']

// Hero templates are picked per hero-banner slide by key (one desktop, one
// mobile) — never by engine targeting — so the only sensible canvases are the
// two the hero surfaces render: 1920×475 desktop and 4:3 mobile.
const HERO_PRESET_VALUES = new Set(['1920x475', '1440x1080'])

// ...and 1920×475 exists for that desktop surface and nothing else, so it is
// off the menu for a matchcard: the engine only ever asks for 16:9 or 4:3, so
// a matchcard on that canvas could never be picked. 4:3 is not hero-only — it
// is a real matchcard canvas as well as the hero mobile one.
const HERO_ONLY_PRESET_VALUES = new Set(['1920x475'])

// A starter body for a new matchcard template. It must satisfy
// tc.artwork.template.v2 as-is: the schema is strict and requires name, canvas,
// targeting and at least one layer, so an empty body couldn't be published.
//
// The canvas is chosen here and only here. Its aspect is what selects the
// template for a 16:9 or 4:3 render, so changing it later silently re-targets
// an existing template — the editor keeps width/height editable for nudging,
// but the preset belongs at the moment of creation.
const newMatchcardBody = (name: string, width: number, height: number) => ({
  name: name || 'New template',
  canvas: { width, height },
  targeting: { categories: [], types: ['singles'], resolution: '1080' },
  layers: [
    {
      id: 'background',
      type: 'image',
      z: 0,
      box: { fill: 'canvas' },
      source: { kind: 'fixed', tc_image_id: null },
    },
  ],
})

// A starter body for a new hero card template (tc.artwork.hero.v1). No
// targeting: hero templates are named by key from a slide, never selected.
const newHeroBody = (name: string, width: number, height: number) => ({
  name: name || 'New hero template',
  canvas: { width, height },
  layers: [
    {
      id: 'background',
      type: 'image',
      z: 0,
      box: { fill: 'canvas' },
      source: { kind: 'fixed', tc_image_id: null },
    },
  ],
})

export default function ArtworkTemplatesList() {
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data, isLoading, error, mutate } = useSWR(
    'artwork-templates',
    fetchArtworkTemplates,
    { revalidateOnFocus: false },
  )
  const templates = useMemo(() => data?.templates ?? [], [data])

  const grouped = useMemo(() => {
    const by: Record<string, ArtworkTemplateSummary[]> = {}
    for (const t of templates) (by[t.template_type] ||= []).push(t)
    return by
  }, [templates])

  const [createOpen, setCreateOpen] = useState(false)
  const [newKey, setNewKey] = useState('')
  const [newName, setNewName] = useState('')
  const [newType, setNewType] = useState<ArtworkTemplateType>('matchcard')
  const [newPreset, setNewPreset] = useState(CANVAS_PRESETS[0].value)
  const [creating, setCreating] = useState(false)

  const handleCreate = async () => {
    const key = newKey.trim()
    if (!key) {
      toast('A key is required', 'error')
      return
    }
    const preset =
      CANVAS_PRESETS.find((p) => p.value === newPreset) ?? CANVAS_PRESETS[0]
    setCreating(true)
    try {
      const starterBody =
        newType === 'matchcard'
          ? newMatchcardBody(newName.trim(), preset.width, preset.height)
          : newType === 'hero'
            ? newHeroBody(newName.trim(), preset.width, preset.height)
            : {}
      const detail = await createArtworkTemplate({
        key,
        template_type: newType,
        name: newName.trim() || null,
        template: starterBody,
      })
      await mutate()
      setCreateOpen(false)
      setNewKey('')
      setNewName('')
      toast('Template created', 'success')
      navigate(`/artwork-templates/${detail.id}`)
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to create template', 'error')
    } finally {
      setCreating(false)
    }
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Couldn’t load artwork templates</h2>
          <p>{(error as Error).message}</p>
          <Button variant="secondary" onClick={() => void mutate()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.pageHeader}>
        <div>
          <h1>Artwork Templates</h1>
          <p>
            The templates the matchcard engine renders from. Edits are
            saved as a draft and only affect live cards once published.
          </p>
        </div>
        <div className={pageStyles.headerButtons}>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            New template
          </Button>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading…</h3>
        </div>
      ) : (
        <div className={styles.groups}>
          {TYPE_ORDER.filter((t) => grouped[t]?.length).map((type) => (
            <div key={type} className={styles.group}>
              <h3 className={styles.groupTitle}>{TYPE_LABELS[type]}</h3>
              <ul className={styles.cardGrid}>
                {grouped[type].map((t) => (
                  <li key={t.id}>
                    {/* A real <a href> so middle-click, cmd-click and
                        open-in-new-tab all work. */}
                    <Link className={styles.card} to={`/artwork-templates/${t.id}`}>
                      <span className={styles.itemName}>{t.name || t.key}</span>
                      <span className={styles.cardKey}>{t.key}</span>
                      <span className={styles.itemMeta}>
                        {t.live_version ? (
                          <Badge variant="live" size="small">
                            Live v{t.live_version.version}
                          </Badge>
                        ) : (
                          <Badge variant="neutral" size="small">
                            Unpublished
                          </Badge>
                        )}
                        {t.draft_version && (
                          <Badge variant="warning" size="small">
                            Draft
                          </Badge>
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {!templates.length && (
            <div className={pageStyles.noData}>
              <p>No templates yet. Create one to get started.</p>
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New template"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void handleCreate()} disabled={creating}>
              Create
            </Button>
          </>
        }
      >
        <div className={styles.createForm}>
          <label>
            Key
            <input
              value={newKey}
              placeholder="e.g. atp_wta_singles_1080"
              onChange={(e) => setNewKey(e.target.value)}
            />
          </label>
          <label>
            Name
            <input
              value={newName}
              placeholder="Human-readable name (optional)"
              onChange={(e) => setNewName(e.target.value)}
            />
          </label>
          <label>
            Type
            <select
              value={newType}
              onChange={(e) => {
                const t = e.target.value as ArtworkTemplateType
                setNewType(t)
                // Each type's canvas list is a subset, so a preset carried
                // over from the other type can be one this list doesn't offer
                // — the select would show its first option while the state
                // still held the old value, and create the template on it.
                if (t === 'hero' && !HERO_PRESET_VALUES.has(newPreset)) {
                  setNewPreset('1920x475')
                } else if (t !== 'hero' && HERO_ONLY_PRESET_VALUES.has(newPreset)) {
                  setNewPreset(CANVAS_PRESETS[0].value)
                }
              }}
            >
              {CREATABLE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </label>
          {(newType === 'matchcard' || newType === 'hero') && (
            <label>
              Canvas
              <select value={newPreset} onChange={(e) => setNewPreset(e.target.value)}>
                {(newType === 'hero'
                  ? CANVAS_PRESETS.filter((p) => HERO_PRESET_VALUES.has(p.value))
                  : CANVAS_PRESETS.filter((p) => !HERO_ONLY_PRESET_VALUES.has(p.value))
                ).map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
              <small>
                {newType === 'hero' ? (
                  <>
                    Pick the surface this template renders for — hero slides
                    take one desktop (1920×475) and one mobile (4:3) template.
                  </>
                ) : (
                  <>
                    The canvas aspect is what selects this template for a
                    render, so it’s fixed at creation. Width and height stay
                    editable in the editor’s settings.
                    {!(
                      CANVAS_PRESETS.find((p) => p.value === newPreset)?.selectable ?? true
                    ) && (
                      <>
                        {' '}
                        <strong>
                          Note: the engine only ever asks for 16:9 or 4:3, so a
                          template on this canvas renders in preview but won’t
                          be picked for a real card yet.
                        </strong>
                      </>
                    )}
                  </>
                )}
              </small>
            </label>
          )}
        </div>
      </Modal>
    </div>
  )
}
